import React, { Component, useEffect, useState, } from 'react';
import { useAsync, useSessionStorage, } from 'react-use';
import { zipObject, pick, findKey, keyBy, omit, last, sortBy, } from 'lodash';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { format as formatDate, startOfMonth, endOfMonth, } from 'date-fns';
import classnames from 'classnames';
import { toast } from 'react-toastify';

import { getDocumentData, } from '../../shared/firebase';
import firebase, { functions } from '../../firebase';
import { pcaCompanyFields, } from '../../shared/models/company';
import { pcaIntegrationFields, } from '../../shared/models/subsidiary';
import { getCollectionData, } from '../../shared/firebase';
import { pcaDataCenters, externalTypes, } from '../../shared/config';
import { fieldsToAddFromExternal, fieldsToAdd, } from '../../shared/models/subsidiary';
import { fields, batchCloneFields, getExtraPartnerValues, } from '../../shared/models/relatedCompanyBalance';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import HelpLink from '../HelpLink';
import CompanyPage from '../hocs/CompanyPage';
import ModalButton from '../ModalButton';
import ModelFormModal from '../modals/ModelFormModal';
import NonFreeeCompanyFormModal from '../modals/NonFreeeCompanyFormModal';
import SubsidiariesSortModal from '../modals/SubsidiariesSortModal';
import AddButton from '../AddButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ProgressButton from '../ProgressButton';
import useLocale from '../hooks/useLocale';

const { entries } = Object;
const getCompanyFreeeCompanies = functions.httpsCallable('getCompanyFreeeCompanies');
const getCompanyBugyoCompanies = functions.httpsCallable('getCompanyBugyoCompanies');
const getBugyoAuthUrl = functions.httpsCallable('getBugyoAuthUrl');
const getMfAuthUrl = functions.httpsCallable('getMfAuthUrl');
const getPcaAuthUrl = functions.httpsCallable('getPcaAuthUrl');
const getPcaDataAreas = functions.httpsCallable('getPcaDataAreas');
const db = firebase.firestore();

export default CompanyPage(function CompanyRelatedCompanies (props) {
  const { history, company, user, match: { params: { companyId } }, yearMonth, } = props;
  const { translate } = useLocale();
  const queryParams = useQueryParams();
  const [bugyoOauthSession, setBugyoOauthSession] = useState(null);
  const { role, enabledAllRelatedCompanies, enabledRelatedCompanyIds, } = company?.users?.[user.id] || {};
  const [bugyoIntegrationPath, setBugyoIntegrationPath] = useSessionStorage('bugyoIntegrationPath');
  const [mfIntegrationPath, setMfIntegrationPath] = useSessionStorage('mfIntegrationPath');
  const location = useLocation();
  const subsidiariesRef = company.ref.collection('subsidiaries');
  const subsidiaries = useCollectionSubscription(subsidiariesRef.orderBy('index'), [company]);
  const isAdmin = user?.admin || ['owner', 'admin'].includes(role);
  const allCompanies = [{ ...company, isParent: true }, ...subsidiaries];
  const companies = allCompanies.filter(_ => isAdmin || enabledAllRelatedCompanies || enabledRelatedCompanyIds?.includes(_.id));
  const { value: freeeCompanies = [] } = useAsync(async () => {
    const { data: { freeeCompanies = [] } } = await getCompanyFreeeCompanies({ companyId: company.id, ownerId: findKey(company.users, { role: 'owner' }), includesExisting: true });
    return freeeCompanies.filter(_ => _.id !== company.sourceId);
  }, []);
  const { value: pcaDataAreas = [] } = useAsync(async () => {
    if(user.pca == null) return [];
    const { data: { pcaDataAreas = [] } } = await getPcaDataAreas();
    return pcaDataAreas;
  }, [user.pca != null]);
  const integrateWithBugyo = async () => {
    setBugyoIntegrationPath(location.pathname);
    const { data: url } = await getBugyoAuthUrl();
    window.location.href = url;
  }
  const integrateWithMf = async () => {
    setMfIntegrationPath(location.pathname);
    const { data: url } = await getMfAuthUrl();
    window.location.href = url;
  }
  const integrateWithPca = async (values) => {
    const { data: url } = await getPcaAuthUrl({ ...values, });
    window.location.href = url;
  }
  useEffect(() => {
    (async () => {
      if(queryParams.mf === '1') {
        if(queryParams.status === 'error') {
          toast.error(`MFクラウド連携に失敗しました: ${queryParams.message || '不明なエラー'}`);
          history.replace(location.pathname);
          return;
        }

        const { sessionId } = queryParams;
        if(sessionId) {
          try {
            const oauthSession = await getDocumentData(db.collection('oauthSessions').doc(sessionId));
            await oauthSession.ref.delete();

            const subsidiaries = await getCollectionData(subsidiariesRef.orderBy('index'));
            await subsidiariesRef.doc().set({
              display_name: oauthSession.office.office_name,
              createdAt: new Date(),
              externalType: 'mf',
              index: (last(subsidiaries)?.index + 1) || 0,
              fiscalYears: oauthSession.accountingPeriods.map(_ => omit(_, 'office_name')),
              mf: {
                ...oauthSession.office,
                accessToken: oauthSession.encryptedAccessToken,
                refreshToken: oauthSession.encryptedRefreshToken,
                tokenType: oauthSession.tokenType,
                expires: oauthSession.expires,
              },
            });
            toast.success('追加しました');
            history.replace(location.pathname);
          } catch (error) {
            toast.error('セッションの取得に失敗しました');
            history.replace(location.pathname);
          }
        }
      }
      if(queryParams.bugyo === '1') {
        if(queryParams.status === 'error') {
          toast.error(`勘定奉行クラウド連携に失敗しました: ${queryParams.message || '不明なエラー'}`);
          history.replace(location.pathname);
          return;
        }

        const { sessionId } = queryParams;
        if(sessionId) {
          try {
            const oauthSession = await getDocumentData(db.collection('oauthSessions').doc(sessionId));
            await oauthSession.ref.delete();
            setBugyoOauthSession(oauthSession);
          } catch (error) {
            toast.error('セッションの取得に失敗しました');
            history.replace(location.pathname);
          }
        }
      }
    })();
  }, []);

  return props.translate(
    <div className="company-subsidiaries container-fluid">
      <div className="p-4 bg-white">
        <div className="d-flex justify-content-end mb-3">
          <HelpLink text="個社別の連結PKGを作成する" />
        </div>
        <div className="d-flex justify-content-center mb-3">
          <h4>グループ会社</h4>
        </div>
        {
          !isAdmin ? null : (
            <div className="d-flex justify-content-end mb-3">
              <ModalButton
                color="secondary"
                Modal={SubsidiariesSortModal}
                modalProps={{ company, subsidiaries, }}
              >
                <span className="fas mr-1 fa-sort" />
                連結子会社の並び替え
              </ModalButton>
            </div>
          )
        }
        {
          !isAdmin ? null :
          subsidiaries.length >= (company.subsidiariesLimit ?? 5) ? (
            <div className="d-flex mb-3 alert alert-warning">
              子会社数の上限に達しています。
            </div>
          ) : (
            <div className="d-flex justify-content-end mb-3 gap-1">
              <AddButton
                label="freeeから連結子会社を追加"
                itemRef={subsidiariesRef.doc()}
                processValues={(values) => {
                  const freeeCompany = freeeCompanies.find(_ => _.id === values.sourceId);
                  return ({
                    ...values,
                    ...omit(freeeCompany, ['id']),
                    index: (last(subsidiaries)?.index + 1) || 0,
                    createdAt: new Date(),
                    externalType: 'freee',
                  });
                }}
                FormModal={ModelFormModal}
                formProps={{ title: '連結子会社 追加', fields: fieldsToAddFromExternal({ externalCompanies: freeeCompanies, }), }}
              />
              <ProgressButton color="primary" process={integrateWithBugyo}>
                <span className="fas mr-1 fa-plus" />
                勘定奉行クラウドから連結子会社を追加
              </ProgressButton>
              <ProgressButton color="primary" process={integrateWithMf}>
                <span className="fas mr-1 fa-plus" />
                MFクラウドから連結子会社を追加
              </ProgressButton>
              {
                user.pca && pcaDataAreas.length > 0 ? (
                  <AddButton
                    label="PCAクラウドから連結子会社を追加"
                    itemRef={subsidiariesRef.doc()}
                    processValues={(values) => {
                      const pcaDataAreasByName = keyBy(pcaDataAreas, 'Name');
                      return {
                        display_name: values.display_name,
                        createdAt: new Date(),
                        index: (last(subsidiaries)?.index + 1) || 0,
                        externalType: 'pca',
                        fiscalYears: sortBy(values.dataAreaIds.map(_ => pcaDataAreasByName[_]).map(_ => ({ ..._, start_date: [_.CompanyTermStart.slice(0, 4), _.CompanyTermStart.slice(4, 6), _.CompanyTermStart.slice(6)].join('-'), end_date: [_.CompanyTermEnd.slice(0, 4), _.CompanyTermEnd.slice(4, 6), _.CompanyTermEnd.slice(6)].join('-'), })), 'start_date'),
                      };
                    }}
                    FormModal={ModelFormModal}
                    formProps={{ title: '連結子会社 追加', fields: pcaCompanyFields({ dataAreas: pcaDataAreas, }), }}
                  />
                ) : (
                  <ModalButton
                    color="primary"
                    Modal={ModelFormModal}
                    modalProps={{
                      title: 'PCAクラウドから連結子会社を追加',
                      fields: pcaIntegrationFields({ pcaDataCenters }),
                      submitLabel: '次へ',
                      onSubmit: integrateWithPca,
                    }}
                  >
                    <span className={classnames('fas mr-1 fa-plus')} />
                    PCAクラウドから連結子会社を追加
                  </ModalButton>
                )
              }
              <AddButton
                label="連結子会社を追加"
                processValues={(_) => ({
                  ..._,
                  externalType: null,
                  index: (last(subsidiaries)?.index + 1) || 0,
                })}
                itemRef={subsidiariesRef.doc()}
                FormModal={ModelFormModal}
                formProps={{ title: '連結子会社 追加', fields: fieldsToAdd, }}
              />
            </div>
          )
        }
        <div>
          {
            companies.length > 0 ? (
              <table className="table table-bordered">
                <thead className="thead-light text-center">
                  <tr>
                    <th rowSpan={2}></th>
                    <th rowSpan={2}>会社名</th>
                    <th colSpan={companies.length}>関係会社マッピング</th>
                  </tr>
                  <tr>
                    {
                      companies.map((company) => {
                        return (
                          <th key={company.id}>
                            {company.display_name}
                          </th>
                        );
                      })
                    }
                  </tr>
                </thead>
                <tbody>
                  {
                    companies.map((relatedCompany, i) => {
                      const { id, ref, isParent, pkgLocks, } = relatedCompany;
                      const isPkgLocked = !!pkgLocks?.[yearMonth];
                      const canLock = isAdmin;
                      const onClickLock = async () => {
                        if(!window.confirm(`本当にロック${isPkgLocked ? '解除' : ''}しますか？`)) return;

                        await ref.set({
                          pkgLocks: {
                            [yearMonth]: !isPkgLocked,
                          },
                        }, { merge: true, });
                        toast.success(`ロック${isPkgLocked ? '解除' : ''}しました`);
                      };

                      return (
                        <tr key={id}>
                          <td style={{ width: company.usesApprovement ? 120 : 30 }}>
                            <div className="d-flex align-items-center justify-content-between gap-1">
                              {
                                company.usesApprovement && canLock && (
                                  <ProgressButton className="text-nowrap" size="sm" process={onClickLock} color={isPkgLocked ? 'warning' : 'success'}>
                                    ロック{isPkgLocked && '解除'}
                                  </ProgressButton>
                                )
                              }
                            </div>
                          </td>
                          <td style={{ minWidth: 250 }}>
                            <Link to={`/companies/${companyId}/relatedCompanies/${relatedCompany.id}${location.search}`}>
                              {relatedCompany.display_name}{isParent && ' (親会社)'}
                            </Link>
                            {relatedCompany.externalType != null && <span className="badge badge-info ml-1">{externalTypes[relatedCompany.externalType]?.label}連携</span>}
                          </td>
                          {
                            companies.map((baseCompany) => {
                              return (
                                <td key={baseCompany.id} style={{ minWidth: 200 }}>
                                  {
                                    entries(getExtraPartnerValues(baseCompany, relatedCompany.id)).map(([companyId, { name, }]) => {
                                      return (
                                        <div key={companyId}>
                                          {name}
                                        </div>
                                      );
                                    })
                                  }
                                </td>
                              );
                            })
                          }
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                連結子会社は未登録です
              </div>
            )
          }
        </div>
      </div>
      {
        bugyoOauthSession && (
          <NonFreeeCompanyFormModal
            isOpen
            onSubmit={async (values) => {
              const bugyoCompany = bugyoOauthSession.companies.find(_ => _.id === values.sourceId);
              await subsidiariesRef.doc().set({
                ...values,
                ...omit(bugyoCompany, ['id']),
                display_name: bugyoCompany.name,
                index: (last(subsidiaries)?.index + 1) || 0,
                createdAt: new Date(),
                externalType: 'bugyo',
                fiscalYears: values.fiscalYears.map(_ => ({ ..._, start_date: formatDate(startOfMonth(_.start_date), 'yyyy-MM-dd'), end_date: formatDate(endOfMonth(_.end_date), 'yyyy-MM-dd'), })),
                bugyo: {
                  accessToken: bugyoOauthSession.encryptedAccessToken,
                  refreshToken: bugyoOauthSession.encryptedRefreshToken,
                  tokenType: bugyoOauthSession.tokenType,
                  expires: bugyoOauthSession.expires,
                },
              });
              toast.success('追加しました');
              history.replace(location.pathname);
              setBugyoOauthSession(null);
            }}
            fields={fieldsToAddFromExternal({ externalCompanies: bugyoOauthSession.companies, })}
            onClickClose={_ => {
              history.replace(location.pathname);
              setBugyoOauthSession(null);
            }}
          />
        )
      }
    </div>
  );
});
